// /dashboard — 4 cards, each independently fetched, per-card error
// isolation per spec §5.

import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import {
  ErrorBanner,
  Skeleton,
  EmptyState,
  StatusBadge,
} from '@/components/primitives';
import {
  formatCurrencyString,
  formatDaysRemaining,
  formatPercentOneDecimal,
} from '@/utils/format';
import { ApiError } from '@/types/errors';

export function DashboardPage() {
  const h1Ref = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => h1Ref.current?.focus(), []);
  return (
    <div>
      <header className="page-header">
        <h1 ref={h1Ref} tabIndex={-1}>
          Dashboard
        </h1>
      </header>
      <div className="dashboard-grid">
        <AgreementsByStatusCard />
        <ActiveValueCard />
        <TeamUtilizationCard />
        <EndingSoonCard />
      </div>
    </div>
  );
}

function AgreementsByStatusCard() {
  const q = useQuery({
    queryKey: qk.dashboardAgreementsByStatus(),
    queryFn: () => dashboardApi.agreementsByStatus(),
  });
  return (
    <section className="section" aria-labelledby="abs-h2">
      <h2 id="abs-h2">Agreements by status</h2>
      {q.isPending ? (
        <Skeleton rows={4} />
      ) : q.isError ? (
        <ErrorBanner
          message="Couldn't load this section."
          onRetry={() => void q.refetch()}
        />
      ) : (q.data?.data.every((r) => r.count === 0) ?? true) ? (
        <EmptyState message="No agreements yet." />
      ) : (
        <table className="data-table">
          <caption className="visually-hidden">Counts by status</caption>
          <thead>
            <tr>
              <th scope="col">Status</th>
              <th scope="col" className="right">
                Count
              </th>
            </tr>
          </thead>
          <tbody>
            {q.data!.data.map((r) => (
              <tr key={r.status}>
                <td>
                  <Link to={`/agreements?status=${r.status}`}>
                    <StatusBadge status={r.status} />
                  </Link>
                </td>
                <td className="right">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function ActiveValueCard() {
  const q = useQuery({
    queryKey: qk.dashboardActiveValue(),
    queryFn: () => dashboardApi.activeValue(),
  });
  return (
    <section className="section" aria-labelledby="av-h2">
      <h2 id="av-h2">Active agreement value</h2>
      {q.isPending ? (
        <Skeleton rows={3} />
      ) : q.isError ? (
        <ErrorBanner
          message="Couldn't load this section."
          onRetry={() => void q.refetch()}
        />
      ) : (
        <>
          <p style={{ fontSize: 24, fontWeight: 700 }}>
            {formatCurrencyString(q.data!.data.total_active_value)}
          </p>
          <dl className="field-list">
            <dt>Fixed-Price total</dt>
            <dd>{formatCurrencyString(q.data!.data.components.fixed_price_total)}</dd>
            <dt>T&amp;M estimated total</dt>
            <dd>{formatCurrencyString(q.data!.data.components.tm_estimated_total)}</dd>
            <dt>T&amp;M without estimate</dt>
            <dd>{q.data!.data.components.tm_without_estimate_count} excluded</dd>
          </dl>
        </>
      )}
    </section>
  );
}

function TeamUtilizationCard() {
  const q = useQuery({
    queryKey: qk.dashboardTeamUtilization({}),
    queryFn: () => dashboardApi.teamUtilization({}),
  });
  return (
    <section className="section" aria-labelledby="tu-h2">
      <h2 id="tu-h2">Team utilization</h2>
      {q.isPending ? (
        <Skeleton rows={5} />
      ) : q.isError ? (
        <ErrorBanner
          message="Couldn't load this section."
          requestId={q.error instanceof ApiError ? q.error.requestId : undefined}
          onRetry={() => void q.refetch()}
        />
      ) : (q.data?.data.length ?? 0) === 0 ? (
        <EmptyState message="No team members with active assignments." />
      ) : (
        <table className="data-table">
          <caption className="visually-hidden">Team utilization</caption>
          <thead>
            <tr>
              <th scope="col">Person</th>
              <th scope="col" className="right">
                Allocation
              </th>
              <th scope="col" className="right">
                Active assignments
              </th>
            </tr>
          </thead>
          <tbody>
            {q.data!.data.map((r) => (
              <tr key={r.person_id}>
                <th scope="row">{r.person_name}</th>
                <td className="right">{formatPercentOneDecimal(r.total_allocation_pct)}</td>
                <td className="right">{r.active_assignment_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function EndingSoonCard() {
  const q = useQuery({
    queryKey: qk.dashboardEndingSoon({}),
    queryFn: () => dashboardApi.endingSoon({}),
  });
  return (
    <section className="section" aria-labelledby="es-h2">
      <h2 id="es-h2">Ending soon</h2>
      {q.isPending ? (
        <Skeleton rows={5} />
      ) : q.isError ? (
        <ErrorBanner
          message="Couldn't load this section."
          requestId={q.error instanceof ApiError ? q.error.requestId : undefined}
          onRetry={() => void q.refetch()}
        />
      ) : (q.data?.data.length ?? 0) === 0 ? (
        <EmptyState message="No agreements ending in the next 30 days." />
      ) : (
        <table className="data-table">
          <caption className="visually-hidden">Agreements ending in the next 30 days</caption>
          <thead>
            <tr>
              <th scope="col">Project</th>
              <th scope="col">Client</th>
              <th scope="col">Days</th>
            </tr>
          </thead>
          <tbody>
            {q.data!.data.map((r) => (
              <tr key={r.agreement_id}>
                <td>
                  <Link to={`/agreements/${r.agreement_id}`}>{r.project_name}</Link>
                </td>
                <td>{r.client_name}</td>
                <td
                  style={{
                    color: r.days_remaining < 0 ? 'var(--c-danger)' : undefined,
                    fontWeight: r.days_remaining < 0 ? 600 : 400,
                  }}
                >
                  {formatDaysRemaining(r.days_remaining)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
